import { TiptapDisplay } from "@skyltmax/ds9"
import { type Extensions } from "@tiptap/core"
import Document from "@tiptap/extension-document"
import Link from "@tiptap/extension-link"
import Placeholder from "@tiptap/extension-placeholder"
import Subscript from "@tiptap/extension-subscript"
import Superscript from "@tiptap/extension-superscript"
import TextAlign from "@tiptap/extension-text-align"
import Typography from "@tiptap/extension-typography"
import Underline from "@tiptap/extension-underline"
import StarterKit from "@tiptap/starter-kit"
import React from "react"
import { type BlockProps } from "."
import { graphql, getFragment } from "@/api"
import StaticContent from "@/common/components/static_content"

const HeadingDocument = Document.extend({ content: "heading" })
const ParagraphDocument = Document.extend({ content: "paragraph+" })

export const columnHeadingStack: Extensions = [
  StarterKit.configure({
    document: false,
    bold: false,
    italic: false,
    heading: {
      levels: [3],
      HTMLAttributes: {
        class: "heading-four mb-2",
      },
    },
  }),
  HeadingDocument,
  Placeholder,
  Subscript,
  Superscript,
  TextAlign.configure({ types: ["heading"] }),
  Typography.configure({
    openDoubleQuote: false,
    closeDoubleQuote: false,
    openSingleQuote: false,
    closeSingleQuote: false,
  }),
]

export const columnParagraphStack: Extensions = [
  StarterKit.configure({
    document: false,
    paragraph: {
      HTMLAttributes: {
        class: "mb-4 paragraph-sm",
      },
    },
  }),
  ParagraphDocument,
  Placeholder,
  Underline,
  Link.configure({ openOnClick: false, HTMLAttributes: { target: "_self", rel: "noopener" } }),
  Subscript,
  Superscript,
  TextAlign.configure({ types: ["paragraph"] }),
  Typography.configure({
    openDoubleQuote: false,
    closeDoubleQuote: false,
    openSingleQuote: false,
    closeSingleQuote: false,
  }),
]

export const LinkColumnsFragment = graphql(`
  fragment FrontpagesLinkColumns on FrontpagesLinkColumnBlock {
    id
    heading

    columns {
      id
      heading
      description
      buttonLabel
      buttonUrl
      imageAlt

      image {
        data
        width
        height
        pngDefault
        pngTwox
        webpDefault
        webpTwox
      }
    }
  }
`)

const LinkColumnsBlock: React.FC<BlockProps<typeof LinkColumnsFragment>> = ({ blockable }) => {
  const block = getFragment(LinkColumnsFragment, blockable)
  const columns = block.columns

  return (
    <StaticContent className="container py-4">
      <h3 className="heading-three mb-8">{block.heading}</h3>

      <div className="grid grid-cols-1 gap-12 lg:grid-cols-3 lg:gap-6">
        {columns.map(column => (
          <div key={column.id}>
            <div className="mb-6 h-32 w-32 overflow-hidden rounded-2xl">
              <picture>
                <source srcSet={`${column.image.webpDefault}, ${column.image.webpTwox} 2x`} type="image/webp" />
                <img
                  srcSet={`${column.image.pngDefault}, ${column.image.pngTwox} 2x`}
                  width={column.image.width || 0}
                  height={column.image.height || 0}
                  alt={column.imageAlt || ""}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </picture>
            </div>

            <TiptapDisplay extensions={columnHeadingStack} json={column.heading} />
            <TiptapDisplay extensions={columnParagraphStack} json={column.description} />

            <a href={column.buttonUrl} className="button button-secondary mb-0" data-gtm="fp-about-us-button">
              {column.buttonLabel}
            </a>
          </div>
        ))}
      </div>
    </StaticContent>
  )
}

export default LinkColumnsBlock
